import { useMutation as useApolloMutation } from '@apollo/client'; // eslint-disable-line no-restricted-imports
import { useCallback, useContext, useMemo, useRef } from 'react';
import { v4 } from 'uuid';
import { useServerErrorHandling } from './useServerErrorHandling';
import { ServiceContext } from 'providers/ServiceProvider';
import { useSimpleError } from 'hooks/apolloWrappers';

const CORRELATION_ID = 'nbb-correlation-id';

const setHeader = (options, key, value) => {
  const headers = options.context?.headers;
  if (headers) {
    options.context.headers = { ...headers, [key]: value };
  } else {
    options.context = { ...options.context, headers: { [key]: value } };
  }
};

const setCorrelationOnRequest = options => {
  const correlationId = v4();
  setHeader(options, CORRELATION_ID, correlationId);
  return correlationId;
};

export const useMutation = (scheme, options, withActiveService = true) => {
  const correlationId = useRef(null);
  const [activeService] = useContext(ServiceContext);
  const showError = useSimpleError();

  // extract custom options
  const { setCorrelation, onCorrelationUsed, serverErrorHandlingOptions, onError, ...restOptions } = options || {};

  const { storeKey } = useServerErrorHandling({
    effect: serverErrorHandlingOptions?.effect || onError || (_ => {}),
    ...serverErrorHandlingOptions
  });

  //custom headers
  if (setCorrelation) {
    correlationId.current = setCorrelationOnRequest(restOptions);
  }

  const mutationOptions = useMemo(() => {
    const result = { ...restOptions };
    if (withActiveService) result.context = { serviceName: activeService?.name };
    result.onError = error => {
      showError(error);
      onError && onError(error);
    };
    return result;
  }, [activeService?.name, onError, restOptions, withActiveService, showError]);

  const [mutationInternal, data] = useApolloMutation(scheme, mutationOptions);

  const mutation = useCallback(
    async input => {
      if (!input) return;

      //extract custom inputs
      const { errorHandlingParams, ...restInput } = input;
      correlationId.current && onCorrelationUsed && onCorrelationUsed(correlationId.current);
      let response = mutationInternal(restInput);

      if (!correlationId.current) {
        // if correlationId is not already set, await the mutation promise and extract the correlation from its response.
        response = await response; // in this point response is a promise
        if (!response) {
          console.error(`Mutation '${scheme.definitions[0].name.value}' response is undefined.`);
          return;
        }

        if (response.data) {
          const { correlationId: correlationIdFromResponse } = response.data[Object.keys(response.data)[0]];
          correlationId.current = correlationIdFromResponse;
        } else correlationId.current = null;
      }

      storeKey({ correlationId: correlationId.current, params: errorHandlingParams });

      //reset correlation id ref
      correlationId.current = null;

      return response;
    },
    [mutationInternal, scheme, storeKey, onCorrelationUsed]
  );

  return [mutation, data];
};
